import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Stack,
  Pagination,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { formatDistanceToNow } from 'date-fns';
import { LazyImage } from './LazyImage';
import { useProject } from '../../contexts/ProjectContext';
import { useSnackbar } from 'notistack';

const ImageLibrary = ({
  loading,
  error,
  imageLibrary,
  libraryParams,
  setLibraryParams,
  totalPages,
  searchInput,
  setSearchInput,
  onImageSelect,
  disabled = false,
  showDownload = false,
  onReusePrompt = null,
  onDeleteImage = null
}) => {
  const { selectedProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleSortChange = (sortBy) => {
    setLibraryParams(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_order: prev.sort_by === sortBy && prev.sort_order === 'desc' ? 'asc' : 'desc',
      show_all: showAllProjects,
      project_id: selectedProject?.id
    }));
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    window.searchTimeout = setTimeout(() => {
      setLibraryParams(prev => ({
        ...prev,
        page: 1,
        search: value,
        show_all: showAllProjects,
        project_id: selectedProject?.id
      }));
    }, 500);
  };

  const getUsageCount = (usage) => {
    if (!usage) return 0;
    return (usage.hero_in_articles?.length || 0) + (usage.used_in_paragraphs?.length || 0);
  };

  const getUsageTooltip = (usage) => {
    if (!usage) return '';
    const parts = [];
    
    if (usage.hero_in_articles?.length) {
      parts.push(`Hero image in: ${usage.hero_in_articles.map(a => a.title).join(', ')}`);
    }
    
    if (usage.used_in_paragraphs?.length) {
      parts.push(`Used in paragraphs: ${usage.used_in_paragraphs.map(p => `${p.article_title} - ${p.heading}`).join(', ')}`);
    }
    
    return parts.join('\n');
  };

  const handleAllProjectsToggle = (e) => {
    setShowAllProjects(e.target.checked);
    setLibraryParams(prev => ({
      ...prev,
      show_all: e.target.checked,
      project_id: selectedProject?.id
    }));
  };

  const handleDownload = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const handleMenuOpen = (event, image) => {
    setAnchorEl(event.currentTarget);
    setSelectedImage(image);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteImage || !selectedImage) {
      setDeleteDialogOpen(false);
      return;
    }

    try {
      await onDeleteImage(selectedImage.id);
    } catch (error) {
      console.error('Error deleting image:', error);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedImage(null);
      setAnchorEl(null);
    }
  };

  const handleDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedImage(null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {!selectedProject?.id ? (
        <>
          <Typography color="error" sx={{ mb: 2 }}>
            Please select a project first to view image library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Each project has its own image library. Select a project from the top navigation bar to view its images.
          </Typography>
        </>
      ) : (
        <>
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search prompts..."
                value={searchInput}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={libraryParams.used_only}
                    onChange={(e) => setLibraryParams(prev => ({
                      ...prev,
                      used_only: e.target.checked
                    }))}
                  />
                }
                label="Used Only"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllProjects}
                    onChange={handleAllProjectsToggle}
                  />
                }
                label="All Projects"
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={libraryParams.sort_by === 'date' ? 'contained' : 'outlined'}
                onClick={() => handleSortChange('date')}
                startIcon={<SortIcon />}
              >
                Date {libraryParams.sort_by === 'date' && (libraryParams.sort_order === 'desc' ? '↓' : '↑')}
              </Button>
              <Button
                size="small"
                variant={libraryParams.sort_by === 'usage' ? 'contained' : 'outlined'}
                onClick={() => handleSortChange('usage')}
                startIcon={<SortIcon />}
              >
                Usage {libraryParams.sort_by === 'usage' && (libraryParams.sort_order === 'desc' ? '↓' : '↑')}
              </Button>
            </Box>
          </Stack>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {imageLibrary.map((image) => (
                  <Grid item xs={12} sm={6} md={4} key={image.id}>
                    <Card sx={{ position: 'relative' }}>
                      <Box sx={{ position: 'relative' }}>
                        <LazyImage
                          imageUrl={image.image_url}
                          alt={image.prompt}
                          sx={{ height: 200, objectFit: 'cover' }}
                        />
                        <Box sx={{ 
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          p: 1,
                          display: 'flex',
                          gap: 1
                        }}>
                          <Tooltip title="Attach Image">
                            <IconButton
                              size="small"
                              onClick={() => onImageSelect(image)}
                              disabled={disabled}
                              sx={{
                                bgcolor: 'background.paper',
                                '&:hover': {
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                },
                              }}
                            >
                              <AddPhotoAlternateIcon />
                            </IconButton>
                          </Tooltip>
                          {showDownload && (
                            <Tooltip title="Options">
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                  },
                                }}
                                onClick={(e) => handleMenuOpen(e, image)}
                                disabled={disabled}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ p: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="caption" 
                            display="block" 
                            noWrap 
                            sx={{ flex: 1 }}
                          >
                            {image.prompt}
                          </Typography>
                          {onReusePrompt && (
                            <Tooltip title="Reuse Prompt">
                              <IconButton
                                size="small"
                                onClick={() => onReusePrompt(image.prompt)}
                                sx={{
                                  '&:hover': {
                                    color: 'primary.main',
                                  },
                                }}
                              >
                                <AutoFixHighIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(image.created_at))} ago
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • {image.source}
                          </Typography>
                          {getUsageCount(image.usage) > 0 && (
                            <Tooltip 
                              title={getUsageTooltip(image.usage)} 
                              arrow
                              placement="top"
                            >
                              <Typography 
                                variant="caption" 
                                color="primary"
                                sx={{ 
                                  cursor: 'help',
                                  fontWeight: 'medium'
                                }}
                              >
                                • used ({getUsageCount(image.usage)})
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={libraryParams.page}
                  onChange={(_, page) => setLibraryParams(prev => ({ ...prev, page }))}
                  color="primary"
                />
              </Box>
            </>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => {
              handleDownload(selectedImage?.image_url);
              handleMenuClose();
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DownloadIcon fontSize="small" />
                <Typography>Download</Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={handleDeleteClick}
              sx={{ color: 'error.main' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon fontSize="small" />
                <Typography>Delete</Typography>
              </Box>
            </MenuItem>
          </Menu>

          <Dialog
            open={deleteDialogOpen}
            onClose={handleDialogClose}
          >
            <DialogTitle>Delete Image</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this image? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button 
                onClick={handleDeleteConfirm} 
                color="error" 
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default ImageLibrary; 